/**
 * Hydrate browser localStorage from DB Sort prefs when signed in, and
 * push local Sort changes to the DB. Guests stay localStorage-only.
 *
 * Scope: shops / orgs / favorites Sort only. Account orders list filters use
 * `readDirectoryOrdersPersist` / `writeDirectoryOrdersPersist` (client session
 * key in searchPersistStorage) — `DirectoryUiPreferenceDTO` has no `orders` field.
 *
 * Hydrate merge rule (G3) — next to useDirectoryUiPreferenceHydrate:
 * - Remote shop key present → apply (DB wins for that key).
 * - Shop keys only in local → keep local; PUT-merge into DB (empty shops:{} never wipes).
 * - orgs/favorites: apply remote if remote ≠ nameAsc OR local is default/absent;
 *   if remote is nameAsc and local is non-default → keep local + PUT-merge.
 * - Explicit remote non-default always wins (cross-device).
 * - G1/G2/G4: if dirty generation changed during GET, skip apply, seed, and putMerge
 *   (user PUT is SSOT; put helpers flush LS before dirty bump so seed/planner see it).
 */

import { useEffect, useRef } from 'react';
import { useRuntimeConfig } from '../../shared/providers/RuntimeConfigProvider.js';
import { useCustomerSession } from '../../shared/session/CustomerSessionProvider.js';
import { isAccountAccessUsable } from './accountApi.js';
import {
  fetchDirectoryUiPreferences,
  putDirectoryUiPreferences,
  type DirectoryUiPreferenceDTO,
  type DirectoryUiPreferencePutBody,
  type DirectoryUiOrgsSortId,
  type DirectoryUiShopsSortId,
} from './directoryUiPreferenceApi.js';
import {
  listDirectoryShopsPersistTenantKeys,
  readDirectoryFavoritesPersist,
  readDirectoryOrgsPersist,
  readDirectoryShopsPersist,
  writeDirectoryFavoritesPersist,
  writeDirectoryOrgsPersist,
  writeDirectoryShopsPersist,
} from '../../shared/session/searchPersistStorage.js';
import { customerLogger } from '../../shared/logging/customerLogger.js';
import type { DirectorySortId } from '../../shared/ui/directoryListFilter.js';

/** Fired after remote Sort prefs are written into localStorage. */
export const DIRECTORY_UI_PREFS_HYDRATED_EVENT = 'rpapp:directory-ui-prefs-hydrated';

/** Default Sort — sparse DB rows and deleted localStorage keys resolve to this. */
const DEFAULT_DIRECTORY_SORT = 'nameAsc' as const;

function isShopsSort(value: string): value is DirectoryUiShopsSortId {
  return (
    value === 'nameAsc' ||
    value === 'nameDesc' ||
    value === 'orgAsc' ||
    value === 'distanceAsc' ||
    value === 'favoriteDesc'
  );
}

function isOrgsSort(value: string): value is DirectoryUiOrgsSortId {
  return value === 'nameAsc' || value === 'nameDesc';
}

function dispatchDirectoryUiPrefsHydrated(): void {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(DIRECTORY_UI_PREFS_HYDRATED_EVENT));
  }
}

export interface DirectoryUiPreferenceMergePlan {
  readonly apply: {
    readonly shops: Readonly<Record<string, DirectoryUiShopsSortId>>;
    readonly orgs?: DirectoryUiOrgsSortId;
    readonly favorites?: DirectoryUiShopsSortId;
  };
  readonly putMerge: DirectoryUiPreferencePutBody;
}

/**
 * Pure G3 planner — no storage writes. Used by hydrate + unit tests.
 */
export function planDirectoryUiPreferenceMerge(
  remote: DirectoryUiPreferenceDTO,
): DirectoryUiPreferenceMergePlan {
  const applyShops: Record<string, DirectoryUiShopsSortId> = {};
  for (const [tenantKey, sort] of Object.entries(remote.shops)) {
    if (isShopsSort(sort)) {
      applyShops[tenantKey] = sort;
    }
  }

  const putMerge: {
    shops?: Record<string, DirectoryUiShopsSortId>;
    orgs?: DirectoryUiOrgsSortId;
    favorites?: DirectoryUiShopsSortId;
  } = {};
  const shopsPut: Record<string, DirectoryUiShopsSortId> = {};

  for (const tenantKey of listDirectoryShopsPersistTenantKeys()) {
    if (Object.prototype.hasOwnProperty.call(remote.shops, tenantKey)) {
      continue;
    }
    const local = readDirectoryShopsPersist(tenantKey);
    if (local !== null && isShopsSort(local.sort)) {
      shopsPut[tenantKey] = local.sort;
    }
  }
  if (Object.keys(shopsPut).length > 0) {
    putMerge.shops = shopsPut;
  }

  const orgsExisting = readDirectoryOrgsPersist();
  const localOrgsSort = orgsExisting?.sort;
  const localOrgsIsDefault =
    localOrgsSort === undefined || localOrgsSort === DEFAULT_DIRECTORY_SORT;
  const remoteOrgsIsDefault = remote.orgs === DEFAULT_DIRECTORY_SORT;
  let applyOrgs: DirectoryUiOrgsSortId | undefined;
  if (!remoteOrgsIsDefault || localOrgsIsDefault) {
    applyOrgs = remote.orgs;
  } else if (isOrgsSort(localOrgsSort)) {
    putMerge.orgs = localOrgsSort;
  }

  const favExisting = readDirectoryFavoritesPersist();
  const localFavSort = favExisting?.sort;
  const localFavIsDefault =
    localFavSort === undefined || localFavSort === DEFAULT_DIRECTORY_SORT;
  const remoteFavIsDefault = remote.favorites === DEFAULT_DIRECTORY_SORT;
  let applyFavorites: DirectoryUiShopsSortId | undefined;
  if (!remoteFavIsDefault || localFavIsDefault) {
    applyFavorites = remote.favorites;
  } else if (isShopsSort(localFavSort)) {
    putMerge.favorites = localFavSort;
  }

  return {
    apply: {
      shops: applyShops,
      ...(applyOrgs !== undefined ? { orgs: applyOrgs } : {}),
      ...(applyFavorites !== undefined ? { favorites: applyFavorites } : {}),
    },
    putMerge,
  };
}

function putMergeIsEmpty(body: DirectoryUiPreferencePutBody): boolean {
  return (
    body.shops === undefined &&
    body.orgs === undefined &&
    body.favorites === undefined
  );
}

/** Apply merge plan to localStorage and dispatch hydrate event (G1 screens). */
function applyMergePlanToLocalStorage(plan: DirectoryUiPreferenceMergePlan): void {
  for (const [tenantKey, sort] of Object.entries(plan.apply.shops)) {
    if (!isShopsSort(sort)) {
      continue;
    }
    const existing = readDirectoryShopsPersist(tenantKey);
    writeDirectoryShopsPersist(
      tenantKey,
      {
        q: existing?.q ?? '',
        sort,
        orgCode: existing?.orgCode ?? null,
      },
      {
        hasUserPosition: false,
        geoPending: sort === 'distanceAsc',
      },
    );
  }

  if (plan.apply.orgs !== undefined) {
    const orgsExisting = readDirectoryOrgsPersist();
    writeDirectoryOrgsPersist({
      q: orgsExisting?.q ?? '',
      sort: plan.apply.orgs,
    });
  }

  if (plan.apply.favorites !== undefined) {
    const favExisting = readDirectoryFavoritesPersist();
    writeDirectoryFavoritesPersist(
      {
        q: favExisting?.q ?? '',
        sort: plan.apply.favorites,
        orgCode: favExisting?.orgCode ?? null,
      },
      {
        hasUserPosition: false,
        geoPending: plan.apply.favorites === 'distanceAsc',
      },
    );
  }

  dispatchDirectoryUiPrefsHydrated();
}

/** G2 — build seed PUT body from every durable shops key + orgs + favorites. */
export function buildDirectoryUiPreferenceSeedBody(): DirectoryUiPreferencePutBody | null {
  const shops: Record<string, DirectoryUiShopsSortId> = {};
  for (const tenantKey of listDirectoryShopsPersistTenantKeys()) {
    const persisted = readDirectoryShopsPersist(tenantKey);
    if (persisted !== null && isShopsSort(persisted.sort)) {
      shops[tenantKey] = persisted.sort;
    }
  }
  const orgs = readDirectoryOrgsPersist();
  const favorites = readDirectoryFavoritesPersist();
  const shopsOut = Object.keys(shops).length > 0 ? shops : undefined;
  const orgsOut =
    orgs !== null && isOrgsSort(orgs.sort) ? orgs.sort : undefined;
  const favoritesOut =
    favorites !== null && isShopsSort(favorites.sort)
      ? favorites.sort
      : undefined;
  if (
    shopsOut === undefined &&
    orgsOut === undefined &&
    favoritesOut === undefined
  ) {
    return null;
  }
  return {
    ...(shopsOut !== undefined ? { shops: shopsOut } : {}),
    ...(orgsOut !== undefined ? { orgs: orgsOut } : {}),
    ...(favoritesOut !== undefined ? { favorites: favoritesOut } : {}),
  };
}

function seedRemoteFromLocal(apiBaseUrl: string): void {
  const body = buildDirectoryUiPreferenceSeedBody();
  if (body === null) {
    return;
  }
  enqueueDirectoryUiPreferencePut(apiBaseUrl, body);
}

/** G4 — bumped on every Sort PUT enqueue; hydrate skips apply if dirty during GET. */
let directoryUiPreferenceDirtyGeneration = 0;

function bumpDirectoryUiPreferenceDirtyGeneration(): void {
  directoryUiPreferenceDirtyGeneration += 1;
}

export function getDirectoryUiPreferenceDirtyGeneration(): number {
  return directoryUiPreferenceDirtyGeneration;
}

/**
 * G1/G2 — true when dirty generation advanced after hydrate GET started.
 * Hydrate uses this (via {@link resolveHydrateRemoteWriteDecision}) to skip
 * seed and putMerge so stale localStorage cannot re-PUT cleared Sort.
 */
export function shouldSkipHydrateRemoteWrites(
  generationAtStart: number,
): boolean {
  return directoryUiPreferenceDirtyGeneration !== generationAtStart;
}

export interface HydrateRemoteWriteDecision {
  readonly apply: boolean;
  readonly seed: boolean;
  readonly putMerge: boolean;
}

/**
 * Pure post-GET decision for hydrate side effects (G1 dirty×putMerge, G2 seed×dirty).
 * When dirty, all remote-write / apply flags are false regardless of plan/seed body.
 */
export function resolveHydrateRemoteWriteDecision(args: {
  readonly generationAtStart: number;
  readonly stored: boolean;
  readonly putMergeEmpty: boolean;
  readonly hasSeedBody: boolean;
}): HydrateRemoteWriteDecision {
  if (shouldSkipHydrateRemoteWrites(args.generationAtStart)) {
    return { apply: false, seed: false, putMerge: false };
  }
  if (!args.stored) {
    return {
      apply: false,
      seed: args.hasSeedBody,
      putMerge: false,
    };
  }
  return {
    apply: true,
    seed: false,
    putMerge: !args.putMergeEmpty,
  };
}

let putQueue: Promise<void> = Promise.resolve();

/**
 * Best-effort PUT of Sort prefs (signed-in only). Debounced via serial queue.
 */
export function enqueueDirectoryUiPreferencePut(
  apiBaseUrl: string,
  body: DirectoryUiPreferencePutBody,
): void {
  bumpDirectoryUiPreferenceDirtyGeneration();
  putQueue = putQueue
    .then(async () => {
      try {
        await putDirectoryUiPreferences(apiBaseUrl, body);
      } catch (err) {
        customerLogger.warn('directory ui preference put failed', err);
      }
    })
    .catch(() => {
      // Keep queue alive after unexpected rejection.
    });
}

export function putDirectoryShopsSortRemote(
  apiBaseUrl: string,
  tenantKey: string,
  sort: DirectorySortId,
): void {
  if (!isShopsSort(sort)) {
    return;
  }
  // Flush durable LS before dirty bump so seed / any non-skipped planner sees post-clear Sort.
  const existing = readDirectoryShopsPersist(tenantKey);
  writeDirectoryShopsPersist(
    tenantKey,
    {
      q: existing?.q ?? '',
      sort,
      orgCode: existing?.orgCode ?? null,
    },
    {
      hasUserPosition: false,
      geoPending: sort === 'distanceAsc',
    },
  );
  enqueueDirectoryUiPreferencePut(apiBaseUrl, {
    shops: { [tenantKey]: sort },
  });
}

export function putDirectoryOrgsSortRemote(
  apiBaseUrl: string,
  sort: DirectorySortId,
): void {
  if (sort !== 'nameAsc' && sort !== 'nameDesc') {
    return;
  }
  const existing = readDirectoryOrgsPersist();
  writeDirectoryOrgsPersist({
    q: existing?.q ?? '',
    sort,
  });
  enqueueDirectoryUiPreferencePut(apiBaseUrl, { orgs: sort });
}

export function putDirectoryFavoritesSortRemote(
  apiBaseUrl: string,
  sort: DirectorySortId,
): void {
  if (!isShopsSort(sort)) {
    return;
  }
  const existing = readDirectoryFavoritesPersist();
  writeDirectoryFavoritesPersist(
    {
      q: existing?.q ?? '',
      sort,
      orgCode: existing?.orgCode ?? null,
    },
    {
      hasUserPosition: false,
      geoPending: sort === 'distanceAsc',
    },
  );
  enqueueDirectoryUiPreferencePut(apiBaseUrl, { favorites: sort });
}

/**
 * When signed in: GET directory Sort prefs → localStorage → notify screens.
 * If no DB row yet, seed from current localStorage (guest → login continuity).
 * Call once from CustomerAppShell (or platform chrome root).
 *
 * Merge rule (G3): see file header. Cross-device prefers DB for explicit remote
 * non-defaults; empty/default remote must not clobber richer local Sort.
 */
export function useDirectoryUiPreferenceHydrate(): void {
  const { apiBaseUrl } = useRuntimeConfig();
  const { session } = useCustomerSession();
  const customerId = session?.customerId;
  const enabled = isAccountAccessUsable(session);
  const hydratedForRef = useRef<number | null>(null);

  useEffect(() => {
    if (!enabled || customerId === undefined) {
      hydratedForRef.current = null;
      return;
    }
    if (hydratedForRef.current === customerId) {
      return;
    }

    const generationAtStart = directoryUiPreferenceDirtyGeneration;
    const ac = new AbortController();
    void fetchDirectoryUiPreferences(apiBaseUrl, ac.signal)
      .then((prefs) => {
        if (ac.signal.aborted) {
          return;
        }
        if (prefs.stored !== true) {
          // G2: dirty after GET start → skip seed (queued clear PUT is SSOT).
          const seedBody = buildDirectoryUiPreferenceSeedBody();
          const decision = resolveHydrateRemoteWriteDecision({
            generationAtStart,
            stored: false,
            putMergeEmpty: true,
            hasSeedBody: seedBody !== null,
          });
          if (decision.seed) {
            seedRemoteFromLocal(apiBaseUrl);
          }
          hydratedForRef.current = customerId;
          return;
        }

        const plan = planDirectoryUiPreferenceMerge(prefs);
        const decision = resolveHydrateRemoteWriteDecision({
          generationAtStart,
          stored: true,
          putMergeEmpty: putMergeIsEmpty(plan.putMerge),
          hasSeedBody: false,
        });
        // G1: dirty → apply/putMerge false — do not re-PUT stale plan.putMerge.
        if (!decision.apply) {
          hydratedForRef.current = customerId;
          return;
        }

        applyMergePlanToLocalStorage(plan);
        if (decision.putMerge) {
          enqueueDirectoryUiPreferencePut(apiBaseUrl, plan.putMerge);
        }
        hydratedForRef.current = customerId;
      })
      .catch((err: unknown) => {
        if (ac.signal.aborted) {
          return;
        }
        customerLogger.warn('directory ui preference hydrate failed', err);
      });

    return () => {
      ac.abort();
    };
  }, [apiBaseUrl, customerId, enabled]);
}
